<script setup lang="ts">
import { computed, ref } from 'vue';
import type { ComponentPublicInstance } from 'vue';
import { Menu, MenuButton, MenuItems } from '@headlessui/vue';
import { $t } from '@/composables/translations/useTranslate.ts';

interface Props {
  placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end' | 'left' | 'right';
  offset?: number;

  maxHeight?: string;
  minHeight?: string;
  minWidth?: string;
  padding?: string;
  background?: string;
  zIndex?: number;

  disabled?: boolean;
  closeOnClick?: boolean;

  triggerAriaLabel?: string;
  menuAriaLabel?: string;

  triggerClass?: string;
  menuClass?: string;
  wrapperClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  placement: 'bottom-start',
  offset: 8,
  maxHeight: 'max-h-[510px]',
  minHeight: 'min-h-[510px]',
  minWidth: 'min-w-[310px]',
  padding: 'p-5',
  background: 'bg-white',
  zIndex: 50,
  disabled: false,
  closeOnClick: true,
  triggerAriaLabel: undefined,
  menuAriaLabel: () => $t('ibe.common.dropdown-menu', 'Dropdown menu'),
  triggerClass: 'w-full',
  menuClass: '',
  wrapperClass: '',
});

const emit = defineEmits<{
  open: [];
  close: [];
  'trigger-click': [event: Event];
}>();

const menuCloseRef = ref<(() => void) | null>(null);
const menuButtonRef = ref<ComponentPublicInstance | null>(null);
const isMenuOpen = ref(false);

const handleOpenStateChange = (open: boolean) => {
  const wasOpen = isMenuOpen.value;
  isMenuOpen.value = open;

  if (open && !wasOpen) {
    emit('open');
  } else if (!open && wasOpen) {
    emit('close');
  }
  return false;
};

const wrapperClasses = computed(() => {
  const classes = ['relative', 'inline-block'];
  if (props.wrapperClass) {
    classes.push(props.wrapperClass);
  }
  return classes.join(' ');
});

const triggerClasses = computed(() => {
  const classes = [];
  if (props.disabled) {
    classes.push('opacity-50', 'cursor-not-allowed');
  }
  if (props.triggerClass) {
    classes.push(props.triggerClass);
  }
  return classes.join(' ');
});

const menuClasses = computed(() => {
  const classes = [
    'absolute',
    'mt-1',
    props.background,
    'shadow-lg',
    'rounded',
    props.minWidth,
    props.minHeight,
    `z-${props.zIndex}`,
  ];

  // Add positioning classes based on placement
  switch (props.placement) {
    case 'bottom-start':
      classes.push('top-full', 'left-0');
      break;
    case 'bottom-end':
      classes.push('top-full', 'right-0');
      break;
    case 'top-start':
      classes.push('bottom-full', 'left-0', 'mb-1', 'mt-0');
      break;
    case 'top-end':
      classes.push('bottom-full', 'right-0', 'mb-1', 'mt-0');
      break;
    case 'left':
      classes.push('right-full', 'top-0', 'mr-1', 'mt-0');
      break;
    case 'right':
      classes.push('left-full', 'top-0', 'ml-1', 'mt-0');
      break;
  }

  if (props.menuClass) {
    classes.push(props.menuClass);
  }

  return classes.join(' ');
});

const contentClasses = computed(() => {
  return [
    props.padding,
    props.maxHeight,
    'overflow-auto',
    'scrollbar-thin',
    'scrollbar-thumb-gray-300',
    'scrollbar-track-transparent',
  ].join(' ');
});

const handleTriggerClick = (event: Event) => {
  if (props.disabled) {
    event.preventDefault();
    return;
  }
  emit('trigger-click', event);
};

const closeDropdown = () => {
  if (menuCloseRef.value) {
    menuCloseRef.value();
  }
};

const openDropdown = () => {
  if (menuButtonRef.value && !props.disabled && !isMenuOpen.value) {
    // MenuButton is a Headless UI component, so we need to access the DOM element via $el
    const buttonEl = menuButtonRef.value.$el as HTMLElement;
    if (buttonEl && typeof buttonEl.click === 'function') {
      buttonEl.click();
    }
  }
};

defineExpose({
  closeDropdown,
  openDropdown,
});
</script>

<template>
  <Menu v-slot="{ open, close }" as="div" :class="wrapperClasses">
    <template v-if="handleOpenStateChange(open) || ((menuCloseRef = close), false)"></template>
    <MenuButton
      ref="menuButtonRef"
      :class="triggerClasses"
      :disabled="disabled"
      :aria-label="triggerAriaLabel"
      @click="handleTriggerClick"
    >
      <slot name="trigger" :open="open" :disabled="disabled"></slot>
    </MenuButton>

    <transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    >
      <MenuItems :class="menuClasses" :aria-label="menuAriaLabel">
        <div :class="contentClasses">
          <slot :close="close" :open="open"></slot>
        </div>
      </MenuItems>
    </transition>
  </Menu>
</template>

<style scoped>
:deep(button):focus {
  outline: 2px solid #6c6b3c;
  outline-offset: 2px;
}
</style>
